import { Menu, Transition } from "@headlessui/react";
import { PlusIcon } from "@heroicons/react/outline";
import { DotsVerticalIcon } from "@heroicons/react/solid";
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import {
  createTutorNote,
  removeNotes,
  updateTutorNote,
} from "../../../api/endpoints/tutors/notes";
import NoteModel from "../../../api/models/NoteModel";
import { classNames } from "../../../helpers";
import { useOrderBy } from "../../../hooks/useOrderBy";
import AppInput from "../../forms/AppInput";
import { getNoteState } from "../helper";

type NotesTabProps = {
  isEditable: boolean;
  notes: NoteModel[];
  sessionId?: string | number;
  listAndSetStudentNotes?: Function;
};
export default function NotesTab(props: NotesTabProps) {
  const {
    isEditable,
    notes,
    sessionId,
    listAndSetStudentNotes = () => {},
  } = props;

  const LocalBasePath = "page.sessionInfo";
  const { t, i18n } = useTranslation("", { keyPrefix: LocalBasePath });
  const [selectedNotes, setSelectedNotes] = useState<NoteModel[]>([]);
  const [useNotes, setNotes] = useState<NoteModel[]>(notes);
  const [noteState, setNoteState] = useState<string>("unpassed");
  const [filterStatus, setFilterStatus] = useState<string>("unpassed");
  const noteContainerRef = useRef<HTMLDivElement>(null);
  const { orderBy, ...sortingHandlers } = useOrderBy("-id");

  useEffect(() => {
    listAndSetStudentNotes(orderBy, setNotes, noteState);
  }, [orderBy, noteState]);

  useEffect(() => {
    setNotes(notes);
    ScrollToTopOfNotes();
  }, [notes]);

  const ScrollToTopOfNotes = () => {
    if (!!noteContainerRef) {
      noteContainerRef?.current?.scroll({
        top: 0,
        behavior: "smooth",
      });
    }
  };
  const addUserNote = async (title: string) => {
    const addNewNote = await createTutorNote({
      session_id: sessionId,
      title: title,
    });
    if (addNewNote.isSuccess) {
      toast?.success(t("noteCreatedSuccessfully"));
      listAndSetStudentNotes(orderBy, setNotes, noteState);
      ScrollToTopOfNotes();
      return addNewNote;
    } else {
      toast?.error(t("unableToCreateNote"));
    }
  };

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm();
  const onSubmit = async ({ note }: any) => {
    const sortNotesById = sortingHandlers.handleSetSortingOrder("-id", false);
    const res = await addUserNote(note);
    if (!!res) {
      orderBy !== "-id"
        ? sortNotesById()
        : listAndSetStudentNotes(orderBy, setNotes, noteState);
    }

    reset();
  };

  const UpdateNoteState = async (state: string, note: NoteModel) => {
    /**
     * on session room  note return with session ID,
     * reading room  notes not return with session ID, return note ID
     * update note state on server need to pass session ID
     * so we need to get session ID to update note state
     */
    const updatedNoteResponse = await updateTutorNote(note.id, {
      session_id: note.session?.id ?? sessionId,
      state: state,
    });
    if (updatedNoteResponse.isSuccess)
      listAndSetStudentNotes(orderBy, setNotes, noteState);
  };
  const removeNote = async (note: NoteModel) => {
    const removeResponse = await removeNotes(note.id);
    if (removeResponse.isSuccess)
      listAndSetStudentNotes(orderBy, setNotes, noteState);
  };
  return (
    <div className="flex flex-col w-full h-[90%] divide-y divide-gray-200">
      <div className="shadow-sm w-full  flex flex-col items-start gap-2 py-2">
        <ul className="flex flex-wrap w-full justify-center   items-center  text-gray-900 dark:text-white py-2">
          <li
            className={classNames(
              "border-gray-200 text-center  w-1/3",
              i18n.dir() === "ltr" ? "border-r" : "border-l"
            )}
          >
            <button
              type="button"
              onClick={() => {
                setNoteState("unpassed");
                setFilterStatus("unpassed");
              }}
              className={classNames(
                "w-full capitalize text-xs leading-4",
                filterStatus === "unpassed"
                  ? "text-gray-500 font-semibold"
                  : "text-brandblue-500"
              )}
            >
              {t("unpassed")}
            </button>
          </li>
          <li
            className={classNames(
              "border-gray-200 text-center  w-1/3",
              i18n.dir() === "ltr" ? "border-r " : " border-l"
            )}
          >
            <button
              type="button"
              className={classNames(
                "w-full capitalize text-xs leading-4",
                filterStatus === "passed"
                  ? "text-gray-500 font-semibold"
                  : "text-brandblue-500"
              )}
              onClick={() => {
                setNoteState("passed");
                setFilterStatus("passed");
              }}
            >
              {t("passed")}
            </button>
          </li>
          <li className="text-center  w-1/3">
            <button
              type="button"
              className={classNames(
                "w-full capitalize text-xs leading-4",
                !!filterStatus
                  ? "text-brandblue-500"
                  : "text-gray-500 font-semibold"
              )}
              onClick={() => {
                setNoteState("");
                setFilterStatus("");
              }}
            >
              {t("everyone")}
            </button>
          </li>
        </ul>
      </div>
      <div
        className="w-full h-full flex flex-col overflow-y-scroll py-2 quraan-viewer"
        ref={noteContainerRef}
      >
        {selectedNotes.length > 0 && (
          <div className="absolute top-0 start-16 flex h-12 items-center gap-3 bg-gray-50 sm:start-20">
            <button
              type="button"
              className="inline-flex items-center rounded border border-gray-300 bg-white px-2.5 py-1.5 text-xs font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-brandblue-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-30"
            >
              {t("editAll")}
            </button>
            <button
              type="button"
              className="inline-flex items-center rounded border border-gray-300 bg-white px-2.5 py-1.5 text-xs font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-brandblue-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-30"
            >
              {t("deleteAll")}
            </button>
          </div>
        )}
        <ul className="w-full h-0 px-0 sm:px-3" data-cy="tutorNotesTable">
          {!!useNotes &&
            useNotes.length > 0 &&
            useNotes?.map((note, index) => {
              return (
                <li
                  key={`NoteInfoTable_${index}_note`}
                  className={classNames(
                    "border-b flex justify-around sm:justify-between py-3 px-4 items-center",
                    selectedNotes.includes(note) && "bg-gray-50"
                  )}
                >
                  <div
                    className={"relative  sm:ps-6 pe-3 text-sm w-1/3 sm:w-6/12"}
                  >
                    <div
                      className={`font-medium  w-full ${
                        selectedNotes.includes(note)
                          ? "text-brandblue-500"
                          : "text-gray-500"
                      }`}
                    >
                      <p className="w-full whitespace-normal">{note.note}</p>
                    </div>
                  </div>
                  <div
                    className={
                      "text-xs text-gray-400 lg:table-cell text-center"
                    }
                  >
                    {getNoteState(note?.status!)}
                  </div>
                  {isEditable && (
                    <div className="pe-4 pb-8 h-2  w-2 px-4 text-gray-400 sm:pe-6 ps-3">
                      <Menu
                        as="div"
                        className="relative inline-block text-left"
                      >
                        <div>
                          <Menu.Button
                            data-cy="updateStatus"
                            className="inline-flex justify-center w-full rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-gray-100 focus:ring-brandblue-500"
                          >
                            <DotsVerticalIcon
                              className="h-3 w-3"
                              aria-hidden="true"
                            />
                          </Menu.Button>
                        </div>
                        <Transition
                          as={"div"}
                          enter="transition ease-out duration-100"
                          enterFrom="transform opacity-0 scale-95"
                          enterTo="transform opacity-100 scale-100"
                          leave="transition ease-in duration-75"
                          leaveFrom="transform opacity-100 scale-100"
                          leaveTo="transform opacity-0 scale-95"
                          dir={"ltr"}
                        >
                          {/* Arrow */}
                          <div className="z-[100] w-4 h-4 border border-l-0 border-b-0 border-black border-opacity-5 absolute ltr:right-[100%] rtl:left-[100%] -top-[0.75rem] -translate-y-full ltr:rotate-45 rtl:rotate-[225deg] bg-white focus:outline-none" />
                          <Menu.Items
                            className={classNames(
                              "z-[99] absolute ltr:right-[120%]   rtl:left-[120%] top-[0.5rem]  mt-2 w-56 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 focus:outline-none",
                              false ? "-translate-y-full" : "-translate-y-[50%]"
                            )}
                          >
                            <div className="p-2 cursor-pointer">
                              <Menu.Item>
                                <button
                                  data-cy="updateStatusPassed"
                                  className="w-full"
                                  onClick={() =>
                                    UpdateNoteState("passed", note)
                                  }
                                >
                                  <h5>{t("passed")}</h5>
                                </button>
                              </Menu.Item>
                            </div>
                            <div className="p-2  cursor-pointer">
                              <Menu.Item>
                                <button
                                  className="w-full"
                                  data-cy="updateStatusUnPassed"
                                  onClick={() =>
                                    UpdateNoteState("unpassed", note)
                                  }
                                >
                                  <h5>{t("unpassed")}</h5>
                                </button>
                              </Menu.Item>
                            </div>
                            <div className="p-2  cursor-pointer">
                              <Menu.Item>
                                <button
                                  className="w-full"
                                  data-cy="deleteNote"
                                  onClick={() => removeNote(note)}
                                >
                                  <h5>{t("delete")}</h5>
                                </button>
                              </Menu.Item>
                            </div>
                          </Menu.Items>
                        </Transition>
                      </Menu>
                    </div>
                  )}
                </li>
              );
            })}
        </ul>
      </div>
      {isEditable && (
        <div
          className="flex items-center justify-between flex-shrink-0 text-sm font-medium text-gray-700"
          dir="rtl"
        >
          <div className=" bg-white  py-4 bottom-0 left-0 w-full">
            <form
              onSubmit={handleSubmit(onSubmit)}
              className="flex gap-2 items-center"
            >
              <AppInput
                hideLabel={true}
                register={register}
                cypressSelector={"noteInput"}
                scehema={{
                  required: true,
                }}
                placeholder={`${t("addNote")}...`}
                type="text"
                name="note"
                id="note"
                className="shadow-sm focus:ring-brandblue-500 focus:border-brandblue-500 block w-full sm:text-sm border-gray-300 rounded-md h-[38px]"
              />
              <div>
                <button
                  type="submit"
                  data-cy="addNote"
                  className="inline-flex items-center justify-center  w-[38px] h-[38px]  border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-brandblue-600 hover:bg-brandblue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brandblue-500"
                >
                  <PlusIcon className="h-5 w-5" />
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
